import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Activity, FlaskConical, Apple, ShieldCheck, Brain, Moon, AlertTriangle,
  Trophy, Users, HeartPulse, Stethoscope, ClipboardList, Calendar, Phone,
  Droplets, Zap, Check, ChevronRight, Sparkles, Award, Target, Shield, FileText,
  TrendingUp, Microscope, Baby,
} from "lucide-react";
import useTheme from "@/utils/useTheme";
import Navbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_83ea495c-8bdb-4889-9a46-037ad22cb3df/artifacts/vlzo0k2e_Dual%20personality.png";

const TEAL = "#4ECDC4";
const CORAL = "#FF6B6B";
const YELLOW = "#FFE66D";
const VIOLET = "#A855F7";

// ---------- Reusable motion primitives ----------
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const staggerChildren = { show: { transition: { staggerChildren: 0.08 } } };

const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? "show" : "hidden"}
      variants={fadeUp} transition={{ delay }} className={className}>
      {children}
    </motion.div>
  );
};

// ---------- Section wrapper ----------
const Section = ({ id, eyebrow, title, subtitle, children, className = "" }) => (
  <section id={id} className={`relative py-20 md:py-28 px-6 ${className}`}>
    <div className="max-w-7xl mx-auto">
      {(eyebrow || title) && (
        <Reveal className="mb-12 md:mb-16 max-w-3xl">
          {eyebrow && (
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#4ECDC4] mb-3">{eyebrow}</p>
          )}
          {title && (
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white leading-[1.1] mb-4">{title}</h2>
          )}
          {subtitle && <p className="text-white/55 text-lg leading-relaxed">{subtitle}</p>}
        </Reveal>
      )}
      {children}
    </div>
  </section>
);

// ---------- Animated stat tile ----------
const StatTile = ({ value, suffix, label, accent }) => (
  <Reveal className="glass-card rounded-2xl p-6 text-center">
    <p className="font-display text-4xl md:text-5xl font-bold mb-1" style={{ color: accent }}>{value}<span className="text-2xl">{suffix}</span></p>
    <p className="text-white/55 text-xs uppercase tracking-[0.15em]">{label}</p>
  </Reveal>
);

// ---------- AGENDA chips (sticky in-page) ----------
const AGENDA = [
  { n: "01", id: "problem", label: "The problem" },
  { n: "02", id: "advantage", label: "Capability advantage" },
  { n: "03", id: "tiers", label: "Three tiers" },
  { n: "04", id: "pillars", label: "Six pillars" },
  { n: "05", id: "lab", label: "Diagnostic lab" },
  { n: "06", id: "pathway", label: "Care pathway" },
  { n: "07", id: "team", label: "Team & AI" },
  { n: "08", id: "stakeholders", label: "Stakeholders" },
  { n: "09", id: "ethics", label: "Ethics & closing" },
];

// ---------- Risks (problem) ----------
const RISKS = [
  { icon: Activity, title: "Vulnerable growth plates", body: "Open epiphyses can be permanently damaged by adult-level stress." },
  { icon: Zap, title: "1.5–2× higher energy needs", body: "Most parents and academies underestimate the calories required." },
  { icon: AlertTriangle, title: "Hidden risk of RED-S", body: "Chronic under-fuelling silently damages bone, hormones, immunity, brain." },
  { icon: Brain, title: "Mental health pressures", body: "Selection anxiety, body image, identity tied to performance." },
  { icon: Users, title: "Misaligned adult incentives", body: "Coaches, agents, parents may push goals that conflict with the child's health." },
];

// ---------- Capability advantage ----------
const CAPABILITIES = [
  { n: "01", title: "Specialist-led", sub: "Pediatric Clinical Care", body: "Pediatricians, sports medicine, dietitians and psychologists working as one team — focused on the growing child athlete.", icon: Stethoscope, color: TEAL },
  { n: "02", title: "Nutrition-based Results", sub: "NIN NutriGuide + In-House Lab", body: "Iron, vitamin D, hormones, allergies, recovery markers — every parameter that affects performance and growth, measured precisely.", icon: FlaskConical, color: CORAL },
  { n: "03", title: "Continuous Oversight", sub: "AI-Powered Monitoring", body: "Food logs, training load, growth trends and automatic red-flag alerts layered on top of clinic visits.", icon: TrendingUp, color: YELLOW },
];

// ---------- Tiers ----------
const TIERS = [
  { tier: "Tier 1", name: "Developing", load: "4–8 hrs/week", level: "School level", examples: "School cricket team, district-level football, junior tennis lessons", accent: "#CD7F32", ring: "ring-amber-700/40" },
  { tier: "Tier 2", name: "Competitive", load: "8–15 hrs/week", level: "State / Zonal level", examples: "Academy cricketers, state-ranked badminton or swimming, age-group athletes", accent: "#C0C0C0", ring: "ring-slate-300/40" },
  { tier: "Tier 3", name: "Elite", load: ">15 hrs/week", level: "National level", examples: "National junior squad members, elite gymnasts, full-time tennis or chess prodigies", accent: TEAL, ring: "ring-[#4ECDC4]/60" },
];

// ---------- Pillars ----------
const PILLARS = [
  { n: "01", icon: TrendingUp, title: "Growth & Development", body: "Quarterly height/weight, Tanner staging, growth velocity, bone age when needed." },
  { n: "02", icon: Microscope, title: "In-House Diagnostic Lab", body: "Five structured panels — measurement before symptoms, results within 24 hours." },
  { n: "03", icon: Apple, title: "Performance Nutrition", body: "Energy availability, sport-specific macros, training-day fuelling rhythm." },
  { n: "04", icon: ShieldCheck, title: "Injury Prevention & Bone Health", body: "Pre-participation screening, red-flag protocol, training-load principles." },
  { n: "05", icon: Brain, title: "Mental Health & Performance Psychology", body: "Routine PHQ-A, GAD-7, burnout and disordered-eating screening." },
  { n: "06", icon: Moon, title: "Recovery, Sleep & Lifestyle", body: "Sleep targets, hydration, heat acclimatisation, travel & tournament protocols." },
];

// ---------- Energy availability zones ----------
const EA_ZONES = [
  { range: "≥ 45", label: "Healthy", color: "#22c55e", bg: "rgba(34,197,94,0.10)" },
  { range: "30 – 45", label: "At risk", color: YELLOW, bg: "rgba(255,230,109,0.10)" },
  { range: "< 30", label: "Clinical RED-S", color: CORAL, bg: "rgba(255,107,107,0.12)" },
];

// ---------- Macros table ----------
const MACROS = [
  { macro: "Carbohydrates", endurance: "6 – 10 g/kg", power: "4 – 7 g/kg" },
  { macro: "Protein", endurance: "1.4 – 1.6 g/kg", power: "1.6 – 2.0 g/kg" },
  { macro: "Fat", endurance: "20 – 30%", power: "≥ 20%" },
];

// ---------- 4R ----------
const FOUR_R = [
  { step: 1, label: "Rehydrate", body: "Within 30–60 min after training" },
  { step: 2, label: "Refuel", body: "Carbs to restore glycogen" },
  { step: 3, label: "Repair", body: "Protein for muscle recovery" },
  { step: 4, label: "Replenish", body: "Electrolytes & micronutrients" },
];

// ---------- Red flags table ----------
const RED_FLAGS = [
  { flag: "Pain at same site > 2 weeks despite rest", cause: "Stress fracture / apophysitis", panels: "Vit D, Calcium, Ferritin + imaging" },
  { flag: "Drop in performance despite training harder", cause: "Overtraining or RED-S", panels: "Panels B + E" },
  { flag: "Unexplained weight loss / stalled growth", cause: "RED-S, eating disorder, hyperthyroidism, celiac", panels: "Panels A + B + C" },
  { flag: "Missed periods (or never started by age 15)", cause: "Female athlete triad", panels: "Panel B (immediately)" },
  { flag: "Repeated minor illnesses, slow recovery", cause: "Suppressed immunity from energy deficit", panels: "Panels A + E" },
  { flag: "Mood changes, loss of interest in sport", cause: "Burnout, depression, hormonal disruption", panels: "Mental health + Panel B" },
  { flag: "GI symptoms before/after training", cause: "Food intolerance, celiac, H. pylori", panels: "Panel C" },
  { flag: "Resting HR ↑ > 5 bpm for several days", cause: "Inadequate recovery / impending overtraining", panels: "Panel E" },
];

// ---------- Care pathway steps ----------
const PATHWAY = [
  { n: 1, icon: ClipboardList, title: "Comprehensive Intake", body: "90-min visit · history · physical · Panel A labs · ECG · diary requested" },
  { n: 2, icon: FileText, title: "Personalised Plan", body: "Within 2 weeks · nutrition · supplementation · recovery · red-flag list" },
  { n: 3, icon: Calendar, title: "Quarterly Reviews", body: "Growth · key labs · energy availability · mental health · load review" },
  { n: 4, icon: Award, title: "Annual Comprehensive Review", body: "Full PPE · full Panel A + B · DEXA when indicated · goal review" },
  { n: 5, icon: Trophy, title: "Pre-Tournament Visit", body: "Panel D 2–3 weeks before · vaccination · last-mile fuelling plan" },
  { n: 6, icon: Phone, title: "On-Demand & Emergency", body: "Hotline · same-day urgent labs · concussion return-to-play protocol" },
];

// ---------- Team ----------
const TEAM = [
  { role: "Lead Pediatrician", body: "Coordinates all care, primary point of contact", icon: Stethoscope, color: TEAL, pin: true },
  { role: "Lab Director / Pathologist", body: "Quality assurance, panel design, interpretation", icon: FlaskConical, color: CORAL, pin: true },
  { role: "Sports Medicine Consultant", body: "Injury, return-to-play, training-load review", icon: Activity, color: YELLOW, pin: true },
  { role: "Sports Dietitian", body: "Energy availability, sport-specific meal plans", icon: Apple, color: TEAL },
  { role: "Pediatric Endocrinologist", body: "Growth, puberty, amenorrhea, RED-S", icon: HeartPulse, color: VIOLET, ref: true },
  { role: "Sports Psychologist", body: "Performance support, anxiety, eating-disorder screen", icon: Brain, color: CORAL },
  { role: "Pediatric Physiotherapist", body: "Movement, prehab, rehab, sport conditioning", icon: ShieldCheck, color: TEAL },
  { role: "Pediatric Cardiologist", body: "ECG abnormalities, family cardiac history", icon: HeartPulse, color: CORAL, ref: true },
  { role: "Pediatric Orthopedist", body: "Growth-plate injuries, stress fractures", icon: Target, color: YELLOW, ref: true },
];

// ---------- Stakeholders ----------
const STAKEHOLDERS = [
  {
    group: "Parents", tagline: "The most important ally", icon: Users, color: TEAL,
    items: [
      "Onboarding session: encouragement vs. pressure",
      "Spotting red flags at home",
      "Lab results in plain language with next steps",
      "Managing parental anxiety so it isn't transferred",
      "Family meal-planning support",
    ],
  },
  {
    group: "Coaches", tagline: "Partners, not adversaries", icon: Trophy, color: YELLOW,
    items: [
      "Plain-language summary letter after every review",
      "Concussion recognition training",
      "RED-S awareness for lean & weight-class sports",
      "Age-appropriate strength training workshops",
      "Clear 'can/cannot do' recommendations",
    ],
  },
  {
    group: "Academies", tagline: "Formal partnership tier", icon: Shield, color: CORAL,
    items: [
      "On-site annual screening days with mobile blood draw",
      "Designated pediatrician on-call for the academy",
      "Standardised emergency protocols",
      "Pre-tournament Panel D for travelling squad",
      "Anonymised academy-wide reports",
    ],
  },
];

// ---------- Ethics ----------
const ETHICS = [
  "The child's long-term health is non-negotiable. If we have to choose between a tournament next month and bone density at age 30, the child wins.",
  "No performance-enhancing supplements for minors. We follow WADA standards. No creatine, fat-burners, or 'edge' substances for any child.",
  "Lab tests are ordered for clinical reasons, not commercial ones. We never run a panel that won't change a decision.",
  "The child has a voice. From age 10, the child is part of every conversation about their body. From age 14, their consent matters as much as their parents'.",
  "We will refuse to clear a child for play if doing so risks serious harm — even under pressure from a parent, coach, or academy.",
  "Confidentiality is real. Mental health, eating concerns, lab values, adolescent issues — never shared with coaches without explicit consent.",
  "We name and report abuse. Coaching that crosses into emotional, physical or sexual abuse is reported through proper channels.",
];

// ---------- References ----------
const REFERENCES = [
  "American Academy of Pediatrics. Overuse Injuries, Overtraining, and Burnout in Young Athletes. Pediatrics. 2024;153(2):e2023065129.",
  "American Academy of Pediatrics. Sports Specialization and Intensive Training in Young Athletes. Pediatrics. 2016;138(3):e20162148.",
  "Mountjoy M et al. The IOC consensus statement: beyond the Female Athlete Triad — Relative Energy Deficiency in Sport (RED-S). Br J Sports Med. 2014;48(7):491–7. (Updated 2018, 2023).",
  "Mountjoy M et al. 2023 International Olympic Committee's (IOC) consensus statement on Relative Energy Deficiency in Sport (REDs). Br J Sports Med. 2023;57(17):1073–98.",
  "Desbrow B et al. Sports Dietitians Australia position statement: Sports nutrition for the adolescent athlete. Int J Sport Nutr Exerc Metab. 2014;24(5):570–84.",
  "Larson-Meyer DE et al. Assessment of nutrient status in athletes and the need for supplementation. Int J Sport Nutr Exerc Metab. 2018;28(2):139–58.",
  "Clénin G et al. Iron deficiency in sports — definition, influence on performance and therapy. Swiss Med Wkly. 2015;145:w14196.",
  "Owens DJ et al. Vitamin D and the athlete: current perspectives and new challenges. Sports Med. 2018;48(Suppl 1):3–16.",
  "Sim M et al. Iron considerations for the athlete: a narrative review. Eur J Appl Physiol. 2019;119(7):1463–78.",
  "HealthyChildren.org (American Academy of Pediatrics). RED-S in Young Athletes: A Parent's Guide to Relative Energy Deficiency in Sport.",
  "Boston Children's Hospital. Relative Energy Deficiency in Sport (REDs) — Clinical Resources.",
  "National Athletic Trainers' Association. Position Statement: Preventing Sudden Death in Sports.",
  "Bergeron MF et al. International Olympic Committee consensus statement on youth athletic development. Br J Sports Med. 2015;49(13):843–51.",
  "DiFiori JP et al. Overuse injuries and burnout in youth sports: a position statement from the American Medical Society for Sports Medicine. Br J Sports Med. 2014;48(4):287–8.",
  "Lloyd RS et al. National Strength and Conditioning Association Position Statement on Long-Term Athletic Development. J Strength Cond Res. 2016;30(6):1491–509.",
  "Concussion in Sport Group. Consensus statement on concussion in sport — the 6th International Conference on Concussion in Sport (Amsterdam, 2022).",
  "World Anti-Doping Agency (WADA) — Prohibited Substances and Methods List (current annual edition).",
  "NIAID Expert Panel. Guidelines for the Diagnosis and Management of Food Allergy in the United States.",
  "Husby S et al. ESPGHAN guidelines for diagnosing coeliac disease. J Pediatr Gastroenterol Nutr. 2020;70(1):141–56.",
];

// ===========================================================================
//                                  PAGE
// ===========================================================================
const YoungAthletesPage = () => {
  const { theme } = useTheme();
  const [, setActive] = useState("problem");

  // Smooth in-page scroll
  const jump = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Highlight active agenda chip on scroll
  useEffect(() => {
    const ids = AGENDA.map(a => a.id);
    const onScroll = () => {
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top <= 200 && r.bottom > 200) { setActive(id); break; }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const bookScreening = () => {
    // If already on homepage, just scroll. Otherwise navigate then scroll after mount.
    if (window.location.pathname === "/") {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth" });
      return;
    }
    sessionStorage.setItem("scrollToOnLoad", "contact");
    window.location.href = "/#contact";
  };

  return (
    <div className="min-h-screen bg-[#0A1029] text-white" data-testid="young-athletes-page">
      <Navbar />

      {/* ============== HERO ============== */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        {/* Background gradient blobs */}
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#4ECDC4]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#FF6B6B]/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-12 items-center">
          <Reveal className="lg:col-span-3">
            <div className="badge-glass inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium mb-6" data-testid="ya-eyebrow">
              <Trophy className="w-3.5 h-3.5 text-[#FFE66D]" />
              <span>Young Athletes Program</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight mb-6">
              Raising Champions,<br />
              <span className="text-[#4ECDC4]">Protecting Children.</span>
            </h1>
            <p className="text-white/60 text-lg md:text-xl leading-relaxed mb-8 max-w-2xl">
              A pediatric sports-medicine program purpose-built for growing athletes — combining
              <strong className="text-white"> specialist clinical care</strong>,
              an <strong className="text-white"> in-house diagnostic lab</strong> and
              <strong className="text-white"> AI-powered monitoring</strong> under one roof at Rev &amp; Dev Children Clinic, Hyderabad.
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-8">
              {["Pediatric clinical care", "In-house diagnostic lab", "AI-powered monitoring"].map(b => (
                <span key={b} className="badge-glass px-3 py-1.5 rounded-full text-xs text-white/70">{b}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={bookScreening}
                className="btn-coral px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2"
                data-testid="ya-hero-cta">
                Book A Screening <ChevronRight className="w-4 h-4" />
              </button>
              <button onClick={() => jump("problem")}
                className="btn-glass px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 text-white"
                data-testid="ya-hero-learn">
                Explore the program
              </button>
            </div>
            <p className="text-white/35 text-sm mt-6 italic" data-testid="ya-tagline">Measured every step.</p>
          </Reveal>

          <Reveal className="lg:col-span-2 relative" delay={0.15}>
            <div className="relative aspect-square w-full rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-[#4ECDC4]/10 bg-gradient-to-br from-white via-[#EAF4FF] to-[#DDE7F5]">
              {/* subtle inner glow */}
              <div className="absolute -top-10 -left-10 w-48 h-48 rounded-full bg-[#4ECDC4]/25 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-12 -right-10 w-56 h-56 rounded-full bg-[#FFE66D]/20 blur-3xl pointer-events-none" />
              <img src={HERO_IMG} alt="Child athlete — two people in one body"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="ya-hero-img" />
              {/* Caption strip */}
              <div className="absolute bottom-0 left-0 right-0 px-5 py-4 bg-gradient-to-t from-[#0A1029] via-[#0A1029]/80 to-transparent">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-white/55">Holistic care</p>
                    <p className="font-display text-2xl text-white leading-snug">Two people in one body.</p>
                  </div>
                  <Baby className="w-8 h-8 text-[#FFE66D] flex-shrink-0" />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============== AGENDA CHIPS (sticky) ============== */}
      <div className="sticky top-[68px] z-30 bg-[#0A1029]/85 backdrop-blur-xl border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="ya-agenda">
          {AGENDA.map(a => (
            <button key={a.id} onClick={() => jump(a.id)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border border-white/10 hover:border-[#4ECDC4]/50 hover:text-white text-white/55 transition"
              data-testid={`ya-agenda-${a.id}`}>
              <span className="text-[#4ECDC4] font-semibold mr-1.5">{a.n}</span>{a.label}
            </button>
          ))}
        </div>
      </div>

      {/* ============== 01 PROBLEM ============== */}
      <Section id="problem" eyebrow="01 — The problem"
        title="A child athlete is two people in one body."
        subtitle="A growing child AND a high-performance athlete — at the same time. This combination creates clinical risks that general pediatric care simply isn't designed to catch.">
        <motion.div variants={staggerChildren} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {RISKS.map((r, i) => (
            <motion.div key={r.title} variants={fadeUp}
              className="glass-card rounded-2xl p-6 group hover:-translate-y-1 transition-transform">
              <div className="w-11 h-11 rounded-xl bg-[#FF6B6B]/12 flex items-center justify-center mb-4 group-hover:scale-110 transition">
                <r.icon className="w-5 h-5 text-[#FF6B6B]" />
              </div>
              <p className="font-display text-lg text-white mb-2">{r.title}</p>
              <p className="text-white/50 text-sm leading-relaxed">{r.body}</p>
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* ============== 02 ADVANTAGE ============== */}
      <Section id="advantage" eyebrow="02 — What makes tots360 different"
        title="The capability advantage."
        subtitle="Most clinics offer one of these. A few offer two. Almost none offer all three under one roof.">
        <div className="grid lg:grid-cols-3 gap-6">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="glass-card rounded-3xl p-7 h-full relative overflow-hidden group">
                <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-10 blur-2xl transition-opacity group-hover:opacity-25"
                  style={{ backgroundColor: c.color }} />
                <p className="font-display text-5xl mb-3" style={{ color: c.color }}>{c.n}</p>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 border border-white/10"
                  style={{ backgroundColor: `${c.color}1f` }}>
                  <c.icon className="w-5 h-5" style={{ color: c.color }} />
                </div>
                <p className="font-display text-xl text-white mb-1">{c.title}</p>
                <p className="text-xs uppercase tracking-[0.15em] mb-3" style={{ color: c.color }}>{c.sub}</p>
                <p className="text-white/55 text-sm leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.25}>
          <div className="mt-8 glass-card rounded-2xl px-6 py-4 flex items-center gap-4 border-l-4 border-[#4ECDC4]">
            <Sparkles className="w-5 h-5 text-[#FFE66D] flex-shrink-0" />
            <p className="text-white/65 text-sm md:text-base">
              <strong className="text-white">The diagnostic lab is the bridge</strong> between the clinic and the AI layer.
              Without it, the program runs on guesses. With it, it runs on data.
            </p>
          </div>
        </Reveal>
      </Section>

      {/* ============== 03 TIERS ============== */}
      <Section id="tiers" eyebrow="03 — Who the program is for"
        title="Three tiers, by training load."
        subtitle="The same core framework — monitoring intensity, lab depth and team scale up with the child's training load.">
        <div className="grid lg:grid-cols-3 gap-6">
          {TIERS.map((t, i) => (
            <Reveal key={t.tier} delay={i * 0.08}>
              <div className={`glass-card rounded-3xl p-7 h-full ring-1 ${t.ring} hover:-translate-y-1 transition-transform group`}>
                <div className="flex items-center justify-between mb-5">
                  <p className="text-xs uppercase tracking-[0.18em] text-white/45">{t.tier}</p>
                  <Trophy className="w-5 h-5 transition group-hover:scale-110" style={{ color: t.accent }} />
                </div>
                <p className="font-display text-3xl text-white mb-1">{t.name}</p>
                <p className="text-2xl font-bold mb-4" style={{ color: t.accent }}>{t.load}</p>
                <p className="text-white/60 text-sm mb-2"><strong className="text-white">Level:</strong> {t.level}</p>
                <p className="text-white/45 text-xs leading-relaxed">{t.examples}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ============== 04 SIX PILLARS ============== */}
      <Section id="pillars" eyebrow="04 — Clinical framework" title="The six pillars of the program.">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.05}>
              <div className="glass-card rounded-2xl p-6 h-full hover:-translate-y-1 transition-transform group">
                <p className="text-xs font-semibold tracking-[0.15em] text-[#4ECDC4] mb-2">PILLAR {p.n}</p>
                <div className="w-12 h-12 rounded-xl bg-[#4ECDC4]/10 border border-[#4ECDC4]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition">
                  <p.icon className="w-5 h-5 text-[#4ECDC4]" />
                </div>
                <p className="font-display text-lg text-white mb-2">{p.title}</p>
                <p className="text-white/55 text-sm leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* ----- Pillar 1 deep-dive ----- */}
        <Reveal className="mt-16">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="glass-card rounded-2xl p-7">
              <p className="text-xs font-semibold tracking-[0.15em] text-[#4ECDC4] mb-2">PILLAR 01 · DEEP-DIVE</p>
              <p className="font-display text-2xl text-white mb-4">What we track</p>
              <ul className="space-y-3">
                {[
                  "Height, weight, BMI plotted on growth charts every 3 months",
                  "Tanner staging for adolescents — biological vs. chronological age",
                  "Sitting height + leg length to detect uneven growth",
                  "Predicted adult height vs. mid-parental target",
                  "Bone age (left wrist X-ray) when delayed maturation suspected",
                  "Menstrual history for adolescent girls",
                ].map(item => (
                  <li key={item} className="flex items-start gap-3 text-white/65 text-sm">
                    <Check className="w-4 h-4 text-[#4ECDC4] mt-0.5 flex-shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="glass-card rounded-2xl p-7 border border-[#FF6B6B]/25 bg-[#FF6B6B]/[0.04]">
              <p className="text-xs font-semibold tracking-[0.15em] text-[#FF6B6B] mb-2">WHY IT MATTERS</p>
              <p className="font-display text-2xl text-white mb-4">A real case scenario</p>
              <p className="text-white/65 text-sm leading-relaxed mb-3">
                A 13-year-old whose periods have stopped — or never started — is <strong className="text-white">not 'just a late bloomer'.</strong>
              </p>
              <p className="text-white/55 text-sm leading-relaxed mb-3">
                She is showing the signs of <span className="text-[#FF6B6B] font-semibold">relative energy deficiency</span>.
                Continuing the same training load will <strong className="text-white">permanently lower her bone mineral density</strong>.
              </p>
              <p className="text-white/65 text-sm leading-relaxed">
                We catch this at the <strong className="text-[#4ECDC4]">3-month review</strong> — not after the stress fracture.
              </p>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* ============== 05 DIAGNOSTIC LAB ============== */}
      <Section id="lab" eyebrow="05 — Pillar 2 · In-house diagnostic laboratory"
        title="The game-changer."
        subtitle='"By the time symptoms appear, performance has already declined and the underlying problem has been silent for months." Measurement converts an invisible risk into a treatable number.'>
        <div className="grid sm:grid-cols-3 gap-5">
          <StatTile value="<24" suffix="h" label="Routine lab turnaround" accent={TEAL} />
          <StatTile value="<4" suffix="h" label="Urgent tests for athletes" accent={CORAL} />
          <StatTile value="5" suffix="" label="Structured athlete panels" accent={YELLOW} />
        </div>

        {/* ----- Pillar 3 Nutrition ----- */}
        <Reveal className="mt-16">
          <div className="glass-card rounded-3xl p-7 md:p-10">
            <p className="text-xs font-semibold tracking-[0.15em] text-[#4ECDC4] mb-2">PILLAR 03 · PERFORMANCE NUTRITION</p>
            <p className="font-display text-2xl md:text-3xl text-white mb-6">Energy availability + sport-specific macros</p>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-white/45 mb-2">Energy Availability formula</p>
                <p className="font-mono text-sm md:text-base text-white/80 bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 mb-4">
                  EA = (Energy In − Exercise Energy) ÷ Fat-Free Mass
                </p>
                <div className="space-y-2">
                  {EA_ZONES.map(z => (
                    <div key={z.label} className="flex items-center gap-3 rounded-lg px-3 py-2 border border-white/8"
                      style={{ backgroundColor: z.bg }}>
                      <span className="font-mono text-sm font-bold w-20 text-right" style={{ color: z.color }}>{z.range}</span>
                      <span className="text-white/45 text-xs">kcal/kg FFM/day</span>
                      <span className="ml-auto text-xs font-semibold uppercase tracking-wider" style={{ color: z.color }}>{z.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-white/45 mb-2">Macros by sport type (per kg body weight)</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-white/40 border-b border-white/10">
                        <th className="py-2 pr-2">Macro</th>
                        <th className="py-2 pr-2 text-[#4ECDC4]">Endurance</th>
                        <th className="py-2 pr-2 text-[#FF6B6B]">Power / Skill</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MACROS.map(m => (
                        <tr key={m.macro} className="border-b border-white/[0.04]">
                          <td className="py-2.5 pr-2 text-white/70">{m.macro}</td>
                          <td className="py-2.5 pr-2 text-white">{m.endurance}</td>
                          <td className="py-2.5 pr-2 text-white">{m.power}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-white/35 text-xs mt-2">Endurance = running, swim, cycle &nbsp;·&nbsp; Power = cricket, tennis, gym</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-3 mb-6">
              {FOUR_R.map(r => (
                <div key={r.step} className="glass-card rounded-xl px-4 py-3 text-center">
                  <p className="font-display text-2xl text-[#4ECDC4]">{r.step}</p>
                  <p className="text-white font-semibold text-sm mt-1">{r.label}</p>
                  <p className="text-white/45 text-xs mt-1">{r.body}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 pt-5 flex items-start gap-3">
              <Droplets className="w-5 h-5 text-[#4ECDC4] flex-shrink-0 mt-0.5" />
              <div className="grid md:grid-cols-3 gap-3 text-sm text-white/55">
                <p><strong className="text-white">Pre:</strong> 400–600 ml in the 2 hours before training</p>
                <p><strong className="text-white">During:</strong> 150–250 ml every 15–20 min</p>
                <p><strong className="text-white">Post:</strong> Replace 150% of weight lost after training</p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* ----- Pillar 4 Red flags table ----- */}
        <Reveal className="mt-16">
          <p className="text-xs font-semibold tracking-[0.15em] text-[#4ECDC4] mb-2">PILLAR 04 · INJURY PREVENTION</p>
          <p className="font-display text-2xl md:text-3xl text-white mb-6">Red flags & the lab panels they trigger</p>
          <div className="glass-card rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.04]">
                  <tr className="text-left text-xs uppercase tracking-wider text-white/45">
                    <th className="py-3 px-4">Red flag</th>
                    <th className="py-3 px-4">Likely cause</th>
                    <th className="py-3 px-4">Lab panels to run</th>
                  </tr>
                </thead>
                <tbody>
                  {RED_FLAGS.map((rf, idx) => (
                    <tr key={idx} className="border-t border-white/[0.06] hover:bg-white/[0.02]">
                      <td className="py-3 px-4 text-white/80">{rf.flag}</td>
                      <td className="py-3 px-4 text-[#FF6B6B] text-xs">{rf.cause}</td>
                      <td className="py-3 px-4 text-[#4ECDC4] text-xs font-semibold">{rf.panels}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-5 glass-card rounded-xl px-5 py-4 flex items-start gap-3">
            <ShieldCheck className="w-4 h-4 text-[#FFE66D] flex-shrink-0 mt-0.5" />
            <p className="text-white/65 text-xs md:text-sm">
              <strong className="text-white">Training-load rules of thumb:</strong> Hours/week ≤ child's age in years · 1 day off/week · 2–3 months/year off primary sport · 10% rule for weekly volume.
            </p>
          </div>
        </Reveal>

        {/* ----- Pillars 5 & 6 ----- */}
        <Reveal className="mt-16">
          <p className="text-xs font-semibold tracking-[0.15em] text-[#4ECDC4] mb-2">PILLARS 05 & 06</p>
          <p className="font-display text-2xl md:text-3xl text-white mb-6">Mental health & recovery — the most-neglected domains.</p>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="glass-card rounded-2xl p-7">
              <div className="flex items-center gap-3 mb-4">
                <Brain className="w-6 h-6 text-[#A855F7]" />
                <p className="font-display text-xl text-white">Pillar 05 · Mental Health & Psychology</p>
              </div>
              <p className="text-xs uppercase tracking-[0.15em] text-white/45 mb-2">Routine screening tools</p>
              <ul className="space-y-2 mb-5">
                {["PHQ-A — depression, every 6 months", "GAD-7 — anxiety, every 6 months", "Athlete Burnout Questionnaire (annual)", "SCOFF for disordered eating in lean sports", "LEAF-Q for adolescent female athletes"].map(i => (
                  <li key={i} className="flex items-start gap-2 text-white/60 text-sm"><Check className="w-4 h-4 text-[#A855F7] mt-0.5" />{i}</li>
                ))}
              </ul>
              <p className="text-xs uppercase tracking-[0.15em] text-white/45 mb-2">Support we offer</p>
              <ul className="space-y-2">
                {["Sports psychologist as routine support, not crisis only", "Coaching parents on talking about losses & pressure", "Warm referrals when clinical signs appear", "Sleep hygiene counselling"].map(i => (
                  <li key={i} className="flex items-start gap-2 text-white/60 text-sm"><Check className="w-4 h-4 text-[#A855F7] mt-0.5" />{i}</li>
                ))}
              </ul>
            </div>

            <div className="glass-card rounded-2xl p-7">
              <div className="flex items-center gap-3 mb-4">
                <Moon className="w-6 h-6 text-[#FFE66D]" />
                <p className="font-display text-xl text-white">Pillar 06 · Recovery, Sleep & Lifestyle</p>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-[#FFE66D]/[0.06] border border-[#FFE66D]/25 rounded-xl p-4 text-center">
                  <p className="font-display text-3xl text-[#FFE66D]">9–11h</p>
                  <p className="text-white/60 text-xs mt-1">ages 6–13</p>
                </div>
                <div className="bg-[#FFE66D]/[0.06] border border-[#FFE66D]/25 rounded-xl p-4 text-center">
                  <p className="font-display text-3xl text-[#FFE66D]">8–10h</p>
                  <p className="text-white/60 text-xs mt-1">ages 14–17</p>
                </div>
              </div>
              <p className="text-xs italic text-white/40 mb-4">Sleep is when growth hormone is released.</p>
              <p className="text-xs uppercase tracking-[0.15em] text-white/45 mb-2">Non-negotiables</p>
              <ul className="space-y-2">
                {["Hydration — daily monitoring, urine colour chart", "At least one low-intensity day per week", "Heat acclimatisation: 10–14 day protocol for >32°C", "Travel & tournament: nutrition, hydration, sleep schedule"].map(i => (
                  <li key={i} className="flex items-start gap-2 text-white/60 text-sm"><Check className="w-4 h-4 text-[#FFE66D] mt-0.5" />{i}</li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* ============== 06 CARE PATHWAY ============== */}
      <Section id="pathway" eyebrow="06 — Care pathway"
        title="How a child moves through the program.">
        <div className="relative">
          {/* center vertical line (desktop) */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#4ECDC4]/40 via-white/10 to-[#4ECDC4]/40 -translate-x-1/2" />
          <div className="space-y-6">
            {PATHWAY.map((p, i) => {
              const left = i % 2 === 0;
              return (
                <Reveal key={p.n} delay={i * 0.05}>
                  <div className={`md:grid md:grid-cols-2 md:gap-12 items-center ${left ? "" : "md:[direction:rtl]"}`}>
                    <div className={`glass-card rounded-2xl p-6 md:[direction:ltr] ${left ? "md:text-right" : ""}`}>
                      <div className={`flex items-center gap-3 mb-2 ${left ? "md:justify-end" : ""}`}>
                        <span className="font-display text-3xl text-[#4ECDC4]">{p.n}</span>
                        <p.icon className="w-5 h-5 text-[#4ECDC4]" />
                      </div>
                      <p className="font-display text-xl text-white mb-1">{p.title}</p>
                      <p className="text-white/55 text-sm">{p.body}</p>
                    </div>
                    <div className="hidden md:block" />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ============== 07 TEAM ============== */}
      <Section id="team" eyebrow="07 — The team"
        title="Multidisciplinary care, coordinated as one.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TEAM.map((m, i) => (
            <Reveal key={m.role} delay={i * 0.04}>
              <div className="glass-card rounded-2xl p-5 h-full hover:-translate-y-1 transition-transform">
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center border border-white/10 flex-shrink-0"
                    style={{ backgroundColor: `${m.color}1f` }}>
                    <m.icon className="w-5 h-5" style={{ color: m.color }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-display text-base text-white">{m.role}</p>
                    {m.ref && <span className="text-[10px] uppercase tracking-wider text-white/35">referral</span>}
                  </div>
                </div>
                <p className="text-white/55 text-xs leading-relaxed">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <div className="glass-card rounded-2xl px-6 py-5 flex items-start gap-4 border-l-4 border-[#FFE66D]">
            <Sparkles className="w-5 h-5 text-[#FFE66D] flex-shrink-0 mt-0.5" />
            <p className="text-white/65 text-sm md:text-base">
              Layered on top: a <strong className="text-white">continuous AI monitoring stack</strong> tracking food logs,
              training load and growth trends — surfacing red-flag alerts the moment any metric drifts out of range.
            </p>
          </div>
        </Reveal>
      </Section>

      {/* ============== 08 STAKEHOLDERS ============== */}
      <Section id="stakeholders" eyebrow="08 — Stakeholders"
        title="Working with parents, coaches and academies.">
        <div className="grid lg:grid-cols-3 gap-6">
          {STAKEHOLDERS.map((s, i) => (
            <Reveal key={s.group} delay={i * 0.08}>
              <div className="glass-card rounded-3xl p-7 h-full hover:-translate-y-1 transition-transform">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-white/10"
                    style={{ backgroundColor: `${s.color}1f` }}>
                    <s.icon className="w-6 h-6" style={{ color: s.color }} />
                  </div>
                  <div>
                    <p className="font-display text-xl text-white">{s.group}</p>
                    <p className="text-xs uppercase tracking-[0.15em]" style={{ color: s.color }}>{s.tagline}</p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {s.items.map(it => (
                    <li key={it} className="flex items-start gap-2 text-white/60 text-sm">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: s.color }} /> {it}
                    </li>
                  ))}
                </ul>
                {s.group === "Academies" && (
                  <button onClick={bookScreening}
                    className="mt-5 inline-flex items-center gap-2 text-[#FF6B6B] text-sm font-semibold hover:gap-3 transition-all"
                    data-testid="ya-academy-cta">
                    Partner with us <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ============== 09 ETHICS + CLOSING ============== */}
      <Section id="ethics" eyebrow="09 — Ethical guardrails"
        title="Where sport, ambition and a child's body meet."
        subtitle="Seven principles we will not bend on.">
        <div className="grid md:grid-cols-2 gap-4">
          {ETHICS.map((e, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <div className="glass-card rounded-2xl p-5 flex gap-4 h-full">
                <span className="font-display text-3xl text-[#4ECDC4] leading-none flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-white/65 text-sm leading-relaxed">{e}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Closing manifesto */}
        <Reveal className="mt-20">
          <div className="relative glass-card rounded-3xl p-8 md:p-14 text-center overflow-hidden">
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-[#4ECDC4]/10 blur-3xl pointer-events-none" />
            <p className="text-xs uppercase tracking-[0.18em] text-[#4ECDC4] mb-6 relative">Closing thoughts</p>
            <p className="font-display text-2xl md:text-3xl lg:text-4xl text-white leading-snug max-w-3xl mx-auto mb-8 relative">
              The story we want to write isn't <em className="text-white/50">"we produced a champion."</em><br />
              It's <span className="text-[#4ECDC4]">"we kept a child healthy, growing, and in love with their sport — and they happened to also become a champion."</span>
            </p>

            <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full border border-white/10 bg-white/[0.04] text-sm text-white/65 mb-8 relative">
              <Stethoscope className="w-4 h-4 text-[#4ECDC4]" /> Pediatric care
              <span className="text-white/20">+</span>
              <FlaskConical className="w-4 h-4 text-[#FF6B6B]" /> In-house lab
              <span className="text-white/20">+</span>
              <TrendingUp className="w-4 h-4 text-[#FFE66D]" /> AI monitoring
            </div>

            <p className="font-display text-3xl md:text-4xl text-white mb-2 relative">RAISING CHAMPIONS, PROTECTING CHILDREN.</p>
            <p className="text-white/40 italic mb-8 relative">Measured every step.</p>

            <div className="flex flex-wrap items-center justify-center gap-3 relative">
              <button onClick={bookScreening} className="btn-coral px-7 py-3 rounded-full font-semibold inline-flex items-center gap-2"
                data-testid="ya-closing-cta">
                Enrol your athlete <ChevronRight className="w-4 h-4" />
              </button>
              <button onClick={bookScreening}
                className="btn-glass px-7 py-3 rounded-full font-semibold inline-flex items-center gap-2 text-white"
                data-testid="ya-closing-secondary">
                Book a screening call
              </button>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* ============== REFERENCES ============== */}
      <section id="references" className="py-20 px-6 border-t border-white/5 bg-white/[0.01]">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-white/45 mb-3">References</p>
            <h2 className="font-display text-2xl md:text-3xl text-white mb-2">Evidence base</h2>
            <p className="text-white/40 text-sm mb-8 max-w-2xl">
              The framework above is grounded in international consensus statements and peer-reviewed pediatric sports-medicine literature.
            </p>
          </Reveal>
          <ol className="space-y-3 list-none counter-reset-refs" data-testid="ya-references">
            {REFERENCES.map((ref, i) => (
              <li key={i} className="flex gap-4 text-sm text-white/55 leading-relaxed">
                <span className="font-mono text-xs text-[#4ECDC4] w-7 flex-shrink-0 pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                <span>{ref}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default YoungAthletesPage;
